<template>
  <div :class="joinClasses('area-chart', [chartStyle])">
    <div class="area-chart__data">
      <apexchart
        ref="dashboardChart"
        height="200"
        :options="chartOptions"
        :series="series"
        type="area"
        width="100%"
        @mounted="mounted"
      />
    </div>

    <!-- Legend -->
    <div
      v-if="showLegend"
      class="area-chart-legend-scroll"
    >
      <div class="area-chart__legend">
        <div
          v-for="(item, index) in series"
          :key="index"
          :class="`area-chart__legend-item area-chart__legend-item--${item.name} typo-tiny`"
        >
          <input
            :id="`${props.chartStyle}_${item.name}`"
            v-model="currentSelections"
            type="checkbox"
            :value="item.name"
            @click="toggleSeries(item.name)"
          >
          <label :for="`${props.chartStyle}_${item.name}`">
            <span class="area-chart__legend-item-swap">
              <span class="area-chart__legend-item-label">
                {{ legendLabel(item) }}
              </span>
              <span class="typo-small-title area-chart__legend-item-count">
                {{ item.count }}
              </span>
            </span>
            <span class="area-chart__legend-item-percent">
              {{ item.percent }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts';
import { ref, watch, onMounted } from 'vue';

import I18n from 'i18n-js';
import joinClasses from '@/functions/join_classes';
import {
  conditionSeriesColours,
  fillOptions,
  getLocales,
  gridOptions,
  markerOptions,
  prioritySeriesColours,
  riskSeriesColours,
  strokeOptions,
  toolBarOptions,
  toolTipOptions,
  xAxisOptions,
  zoomOptions,
  totalSeriesColours,
} from '@/functions/chart_config';

const apexchart = VueApexCharts;
const dashboardChart = ref('dashboardChart');
const currentSelections = ref([]);

const props = defineProps({
  /** Defines the style of the chart eg. `condition` or `risk`. This sets the correct chart colours, tooltip text, legend labels and active series. */
  chartStyle: { type: String, required: false, default: null },
  /** Data for the chart series */
  series: { type: Array, required: true },
  /** Hides or Shows Legend */
  showLegend: { type: Boolean, required: false, default: true },
});

const emit = defineEmits(['CSVparamUpdate']);

/**
 * TODO: Find a better way to get the mounting of apexchart tested (mock?)
 * Apexchart stubbed for now
 */
function mounted() {
  if (props.series?.length === 0) return;
  props.series.forEach((item) => dashboardChart.value.toggleSeries(item?.name));

  dashboardChart.value.toggleSeries(props.series[0]?.name);

  if (props.chartStyle === 'condition' && props.series[1]) {
    dashboardChart.value.toggleSeries(props.series[1].name);
  }
}

const DEFAULT_SELECTIONS = {
  condition: [5, 4],
  priority: ['X'],
  risk: ['high'],
};

onMounted(() => {
  currentSelections.value = DEFAULT_SELECTIONS[props.chartStyle] ?? [5, 4];
  emit('CSVparamUpdate', currentSelections.value, props.chartStyle);
});

watch(
  () => currentSelections.value,
  () => {
    emit('CSVparamUpdate', currentSelections.value, props.chartStyle);
  }
);

/**
 * Toggles graph series for the graph and legend
 */
function toggleSeries(checkbox) {
  dashboardChart.value.toggleSeries(checkbox);
}

/**
 * Returns the correct label text for the legend
 */
function legendLabel(item) {
  if (item.name === 'all') return I18n.t('general.total');
  if (item.label) return item.label;

  if (props.chartStyle === 'condition') {
    return `${I18n.t('operations.condition')} ${item.name}`;
  }

  return I18n.t(`risk_levels.${item.name}`);
}

/**
 * Looks up a server-resolved label by series name. The apex tooltip formatter
 * only receives the name, not the series object.
 */
function seriesLabel(seriesName) {
  return props.series?.find((item) => String(item.name) === String(seriesName))?.label;
}

/**
 * Chart options
 * reference: https://apexcharts.com/docs/  -> Options (reference)
 */
const chartOptions = {
  chart: {
    id: props.chartStyle,
    foreColor: '#989799',
    toolbar: toolBarOptions,
    zoom: zoomOptions,
    defaultLocale: I18n.locale,
    locales: getLocales(I18n),
  },
  yaxis: {
    min: 0,
    labels: {
      formatter: function (val) {
        return val ? val.toFixed(0) : val;
      },
    },
  },
  xaxis: xAxisOptions,
  legend: { show: false },
  dataLabels: { enabled: false },
  markers: markerOptions,
  stroke: strokeOptions,
  grid: gridOptions,
  toolBarOptions,
  colors: [],
  fill: fillOptions,
  tooltip: {
    ...toolTipOptions,
    y: {
      title: {
        // Returns the correct format for the chart tooltip text
        formatter: (seriesName) => {
          let label = seriesLabel(seriesName);
          if (label) return `${label}:`;

          if (props.chartStyle === 'condition') {
            return `${I18n.t('operations.condition')} ${seriesName}:`;
          }
          return `${I18n.t(`risk_levels.${seriesName}`)}:`;
        },
      },
    },
  },
};

/**
 * Override chart options based on the chartStyle (condition/risk);
 */

if (!props.showLegend) {
  chartOptions.colors = totalSeriesColours;
  chartOptions.markers.colors = totalSeriesColours;
  chartOptions.fill.colors = totalSeriesColours;
} else if (props.chartStyle === 'condition') {
  chartOptions.colors = conditionSeriesColours;
  chartOptions.markers.colors = conditionSeriesColours;
  chartOptions.fill.colors = conditionSeriesColours;
} else if (props.chartStyle === 'priority') {
  chartOptions.colors = prioritySeriesColours;
  chartOptions.markers.colors = prioritySeriesColours;
  chartOptions.fill.colors = prioritySeriesColours;
} else {
  chartOptions.colors = riskSeriesColours;
  chartOptions.markers.colors = riskSeriesColours;
  chartOptions.fill.colors = riskSeriesColours;
}
</script>
